// Import React and useState for local state handling
import React, { useState } from "react";
// Import modal component to show full news content
import NewsModal from "./NewsModal";
// Custom hook to fetch comment counts for each article
import useComments from "../hooks/useComments";

// List of allowed categories for tagging articles
const ALLOWED_CATEGORIES = ["sports", "technology", "politics", "health", "business", "entertainment"];

// NewsItem component renders a single article card
const NewsItem = ({ article }) => {
  const [showModal, setShowModal] = useState(false); // State to control modal visibility

  // Function to clean the description and extract any "Read More" link
  const processDescription = () => {
    const parser = new DOMParser(); // Create DOM parser
    const doc = parser.parseFromString(article.description, "text/html"); // Parse HTML string
    const aTag = doc.querySelector("a"); // Look for any anchor tag inside description
    const readMoreLink = aTag ? aTag.href : ""; // Extract href if exists
    if (aTag) aTag.remove(); // Remove the anchor from the description for clean display
    return {
      cleanedDescription: doc.body.innerHTML, // Return cleaned HTML content
      readMoreLink, // Return extracted read more link
    };
  };

  // Process description once
  const { cleanedDescription, readMoreLink } = processDescription();

  // Determine article category and normalize it
  let categoryRaw = article.category ? article.category.trim().toLowerCase() : "";
  let category = ALLOWED_CATEGORIES.find(label => categoryRaw.includes(label)) || "";

  // Fetch comments using custom hook based on article link
  const { comments } = useComments(article.link);

  return (
    <>
      {/* Main container for the news item, flex for image + text layout */}
      <div className="flex flex-col sm:flex-row gap-4 border-b pb-6 mb-6 hover:shadow-sm transition-all">
        
        {/* Left Image Section */}
        <div className="relative w-full sm:w-1/3 h-52 sm:h-auto">
          {/* Display article image, object-cover ensures it fills container */}
          <img src={article.image} alt={article.title} className="w-full h-full object-cover rounded-md" />
          
          {/* Category badge, only shown if category exists */}
          {category && (
            <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
              {category.toUpperCase()}
            </span>
          )}
        </div>

        {/* Right Text Section */}
        <div className="flex-1">
          {/* Article title */}
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{article.title}</h2>
          
          {/* Article description, using dangerouslySetInnerHTML to render HTML content */}
          <p className="text-gray-700 text-sm mb-3" dangerouslySetInnerHTML={{ __html: cleanedDescription }}></p>
          
          {/* Footer row with publication date, comment count, and read more button */}
          <div className="flex justify-between items-center text-xs text-gray-500">
            <span>{new Date(article.pubDate).toLocaleString()}</span> {/* Display formatted pub date */}
            <span className="text-xs text-gray-500">{comments.length} comment{comments.length !== 1 ? 's' : ''}</span> {/* Display comment count */}
            <button
              onClick={() => setShowModal(true)} // Open modal on click
              className="text-sm text-white bg-black hover:bg-gray-800 px-3 py-1 rounded-md"
            >
              Read More
            </button>
          </div>
        </div>
      </div>

      {/* Modal for reading full article */}
      {showModal && (
        <NewsModal
          show={showModal} // Pass modal visibility state
          handleClose={() => setShowModal(false)} // Callback to close modal
          article={{ ...article, link: readMoreLink || article.link }} // Use extracted readMoreLink if available
          category={category} // Pass category for display inside modal
        />
      )}
    </>
  );
};

// Export NewsItem for use in other components
export default NewsItem;
